import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { AlertOctagon, AlertTriangle, Check, ChevronsUpDown, Eye, EyeOff, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import type { WriteError } from "../api";
import { normalizeName } from "../model";
import type { CatalogIngredient } from "../types";

/** One ingredient of a recipe: solid when the detector checks it, dashed and muted when it cannot see it. */
export function IngredientChip({
  ingredient,
  fallbackId,
  afterBaking = false,
  className,
}: {
  ingredient: CatalogIngredient | undefined;
  /** shown when the ingredient is unknown */
  fallbackId?: string;
  afterBaking?: boolean;
  className?: string;
}) {
  const { t } = useLocalization();
  const detectable = !!ingredient?.detector_classes.length;
  const label = ingredient?.name ?? fallbackId ?? "?";
  return (
    <span
      className={cn(
        "inline-flex h-6 max-w-full items-center gap-1 rounded-md border px-1.5 text-xs",
        detectable ? "border-border bg-muted/60 text-foreground" : "border-dashed text-muted-foreground",
        !ingredient && "border-red-500/40 text-red-300",
        className,
      )}
      title={
        detectable
          ? t("catalog.chip.detectable", { classes: ingredient!.detector_classes.join(", ") })
          : t("catalog.chip.notDetectable")
      }
    >
      {detectable ? <Eye className="h-3 w-3 shrink-0 opacity-70" aria-hidden /> : <EyeOff className="h-3 w-3 shrink-0 opacity-60" aria-hidden />}
      <span className="truncate">{label}</span>
      {afterBaking && (
        <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[10px] font-medium text-amber-300">
          {t("catalog.chip.afterBaking")}
        </span>
      )}
    </span>
  );
}

/** A labelled field of an editor. */
export function Field({ label, hint, htmlFor, children }: { label: ReactNode; hint?: ReactNode; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Why a save failed: the backend's problems of the catalog, a conflict (with a reload) or another error. */
export function WriteErrorBlock({ error, onReload }: { error: WriteError | null; onReload?: () => void }) {
  const { t } = useLocalization();
  if (!error) return null;
  const conflict = error.kind === "conflict";
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col gap-1.5 rounded-md border px-3 py-2 text-xs",
        conflict ? "border-amber-500/35 bg-amber-500/10 text-amber-200" : "border-red-500/30 bg-red-500/10 text-red-300",
      )}
    >
      <div className="flex items-start gap-2">
        {conflict ? <AlertTriangle className="mt-px h-4 w-4 shrink-0" aria-hidden /> : <AlertOctagon className="mt-px h-4 w-4 shrink-0" aria-hidden />}
        <span className="min-w-0 flex-1 break-words">
          {conflict
            ? t("catalog.save.conflict", { error: error.message })
            : error.kind === "invalid"
              ? t("catalog.save.invalid")
              : t("catalog.save.failed", { error: error.message })}
        </span>
        {conflict && onReload && (
          <Button size="sm" variant="outline" className="h-7 shrink-0 px-2 text-xs" onClick={onReload}>
            {t("catalog.save.reload")}
          </Button>
        )}
      </div>
      {error.kind === "invalid" && (
        <ul className="ml-6 list-disc space-y-0.5 font-mono text-[11px]">
          {error.problems.map((problem) => (
            <li key={problem} className="break-words">
              {problem}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A list of short texts edited as chips: Enter (or the plus) adds what is typed, the cross removes one.
 * ``suggestions`` are offered below the input while typing.
 */
export function TagEditor({
  values,
  onChange,
  placeholder,
  suggestions = [],
  renderTag,
  id,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  suggestions?: string[];
  renderTag?: (value: string) => ReactNode;
  id?: string;
}) {
  const { t } = useLocalization();
  const [text, setText] = useState("");
  const matches = useMemo(() => {
    const query = normalizeName(text);
    const taken = new Set(values.map(normalizeName));
    return suggestions
      .filter((s) => !taken.has(normalizeName(s)) && (!query || normalizeName(s).includes(query)))
      .slice(0, 8);
  }, [suggestions, text, values]);

  const add = (value: string) => {
    const clean = value.trim();
    if (!clean) return;
    if (!values.some((v) => normalizeName(v) === normalizeName(clean))) onChange([...values, clean]);
    setText("");
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      add(text);
    } else if (event.key === "Backspace" && !text && values.length) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value) => (
            <span key={value} className="inline-flex h-7 max-w-full items-center gap-1 rounded-md border bg-muted/50 pl-2 pr-0.5 text-xs">
              <span className="truncate">{renderTag ? renderTag(value) : value}</span>
              <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                onClick={() => onChange(values.filter((v) => v !== value))}
                aria-label={t("catalog.tags.remove", { value })}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          id={id}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-9"
          autoComplete="off"
        />
        <Button type="button" variant="outline" className="h-9 w-9 shrink-0 p-0" onClick={() => add(text)} disabled={!text.trim()} aria-label={t("catalog.tags.add")}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {matches.length > 0 && (text || suggestions.length <= 40) && (
        <div className="flex flex-wrap gap-1">
          {matches.map((match) => (
            <button
              key={match}
              type="button"
              onClick={() => add(match)}
              className="inline-flex h-6 items-center gap-1 rounded-md border border-dashed px-1.5 text-xs text-muted-foreground hover:border-solid hover:text-foreground"
            >
              <Plus className="h-3 w-3" aria-hidden />
              {match}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Picks one ingredient: a button that opens a filterable list (name, aliases and id match). */
export function IngredientPicker({
  ingredients,
  value,
  onChange,
  taken,
  placeholder,
}: {
  ingredients: CatalogIngredient[];
  value: string | null;
  onChange: (id: string) => void;
  /** ids already in the recipe: listed, but marked */
  taken?: Set<string>;
  placeholder: string;
}) {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = ingredients.find((i) => i.id === value);
  const shown = useMemo(() => {
    const q = normalizeName(query);
    const list = q
      ? ingredients.filter((i) => [i.id, i.name, ...i.aliases].some((name) => normalizeName(name).includes(q)))
      : ingredients;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [ingredients, query]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="h-9 w-full min-w-0 justify-between px-2.5 font-normal">
          <span className={cn("flex min-w-0 items-center gap-1.5 truncate", !selected && "text-muted-foreground")}>
            {selected ? (
              <>
                {selected.detector_classes.length ? (
                  <Eye className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                ) : (
                  <EyeOff className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
                )}
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              value ?? placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(22rem,calc(100vw-2rem))] p-0" align="start">
        <div className="border-b p-2">
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("catalog.picker.search")}
            className="h-8"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1" role="listbox">
          {shown.length === 0 && <div className="px-2 py-3 text-center text-xs text-muted-foreground">{t("catalog.picker.none")}</div>}
          {shown.map((ingredient) => {
            const isSelected = ingredient.id === value;
            const isTaken = !isSelected && taken?.has(ingredient.id);
            return (
              <button
                key={ingredient.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(ingredient.id);
                  setOpen(false);
                  setQuery("");
                }}
                className={cn(
                  "flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                  isSelected && "bg-accent",
                )}
              >
                <Check className={cn("h-3.5 w-3.5 shrink-0", isSelected ? "opacity-100" : "opacity-0")} aria-hidden />
                <span className="min-w-0 flex-1 truncate">{ingredient.name}</span>
                {isTaken && <span className="shrink-0 text-[10px] text-muted-foreground">{t("catalog.picker.taken")}</span>}
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {ingredient.detector_classes.length ? ingredient.detector_classes.join(", ") : t("catalog.picker.notDetectable")}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** An editor dialog's footer: the error, Cancel and Save. */
export function EditorFooter({
  error,
  onReload,
  saving,
  canSave,
  onCancel,
  onSave,
  hint,
}: {
  error: WriteError | null;
  onReload?: () => void;
  saving: boolean;
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
  hint?: ReactNode;
}) {
  const { t } = useLocalization();
  return (
    <div className="flex flex-col gap-3">
      <WriteErrorBlock error={error} onReload={onReload} />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
        {hint && <p className="text-xs text-muted-foreground sm:mr-auto">{hint}</p>}
        <Button variant="outline" className="h-10 sm:h-9" onClick={onCancel} disabled={saving}>
          {t("catalog.save.cancel")}
        </Button>
        <Button className="h-10 sm:h-9" onClick={onSave} disabled={!canSave || saving}>
          {saving ? t("catalog.save.saving") : t("catalog.save.save")}
        </Button>
      </div>
    </div>
  );
}
